import React, { useState, useRef, Fragment, useEffect } from 'react';
import Loading from 'react-loading-components';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { format } from 'date-fns';
import NotificationSystem from 'react-notification-system';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import moment from 'moment';
import sumBy from 'lodash/sumBy';

import { ButtonLink, Button } from 'components/Button';
import Edit from 'components/Icons/Edit';
import Check from 'components/Icons/Check';
import Delete from 'components/Icons/Delete';
import Cancel from 'components/Icons/Cancel';
import Close from 'components/Icons/Close';
import Input from 'components/Form/Input';
import MaskedInput from 'components/Form/MaskedInput';
import { formatCurrency } from 'utils/formatCurrency';

import style from './style.scss';

const currencyMask = createNumberMask({
  prefix: '$ ',
  allowDecimal: true,
});

const unmask = val => val.replace(/[$, ]+/g, '');

const EditableTable = (props) => {
  const { rows } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [creditorName, setCreditorName] = useState('');
  const [currentCreditor, setCurrentCreditor] = useState('');
  const [settlementId, setSettlementId] = useState('');
  const [creditAccountNumber, setCreditAccountNumber] = useState('');
  const [accountBalance, setAccountBalance] = useState('');
  const [amount, setAmount] = useState('');
  const [settlementDate, setSettlementDate] = useState('');
  const [id, setId] = useState('');
  const [dscFee, setDscFee] = useState('');
  const [custodianFee, setCustodianFee] = useState('');

  const notification = useRef(null);
  const newEstimated = useRef(null);
  const newActual = useRef(null);
  const creditorNameRef = useRef(null);
  const currentCreditorRef = useRef(null);
  const settlementIdRef = useRef(null);
  const creditAccountNumberRef = useRef(null);
  const accountBalanceRef = useRef(null);
  const dscFeeRef = useRef(null);
  const custodianFeeRef = useRef(null);
  const settlementAmount = useRef(null);
  const estimated = useRef(null);
  const actual = useRef(null);
  const settlementDateRef = useRef(null);

  useEffect(() => {
    if (props.testSubject && props.testSubject.value) {
      const values = props.testSubject.value;
      props.saveRow({
        creditorName: values.originalCreditor,
        currentCreditor: values.currentCreditor,
        settlementId: values.settlementID,
        creditAccountNumber: values.accountNoLast4,
        accountBalance: unmask(values.originalAccountBalanceEnrolled) || 0,
        amount: unmask(values.payoffAmountToCreditor) || 0,
        dscFee: unmask(values.amountDueToDSC) || 0,
        custodianFee: unmask(values.custodianFee) || 0,
        isActual: values['estimated/Actual'] === 'Actual',
        settlementDate: moment().add(2, 'days').format('MM/DD/YYYY'), // today+2
      });
    }
  }, [props.testSubject]);

  const handleEditRow = (index, e) => {
    e.preventDefault();
    setIsEditing(index);
    setId(props.rows[index].id);
    setCreditorName(props.rows[index].creditorName);
    setCurrentCreditor(props.rows[index].currentCreditor);
    setSettlementId(props.rows[index].settlementId);
    setCreditAccountNumber(props.rows[index].creditAccountNumber);
    setAccountBalance(props.rows[index].accountBalance);
    setAmount(props.rows[index].amount);
    setDscFee(props.rows[index].dscFee);
    setCustodianFee(rows[index].custodianFee);
    setSettlementDate(format(new Date(props.rows[index].settlementDate), 'MM/dd/yyyy') || '');
  };

  const handleUpdateRow = (index, e) => {
    e.preventDefault();
    const creditorNameTemp = (
      creditorName && creditorName.trim()
    ) || '';
    const currentCreditorTemp = (
      currentCreditor && currentCreditor.trim()
    ) || '';
    const settlementIdTemp = (
      settlementId && settlementId.trim()
    ) || '';

    if (
      !creditorNameTemp ||
      !currentCreditorTemp ||
      !settlementIdTemp ||
      !creditAccountNumber ||
      !accountBalance ||
      amount === '' ||
      (!newActual.current.checked && !newEstimated.current.checked) ||
      !settlementDate ||
      dscFee === '' ||
      custodianFee === ''
    ) {
      notification.current
      && notification.current.addNotification({
        message: 'All the fields are required',
        level: 'error',
        position: 'tc',
      });
      return false;
    }
    if (creditAccountNumber.replace('_', '').length < 4) {
      notification.current
      && notification.current.addNotification({
        message: 'Account number is not valid',
        level: 'error',
        position: 'tc',
      });
      return false;
    }
    if (settlementDate.indexOf('_') !== -1 || !moment(settlementDate, 'MM/DD/YYYY').isValid()) {
      notification.current
      && notification.current.addNotification({
        message: 'Date of Settlement is not valid',
        level: 'error',
        position: 'tc',
      });
    } else {
      props.updateRow(index, {
        creditorName: creditorNameTemp,
        currentCreditor: currentCreditorTemp,
        settlementId: settlementIdTemp,
        creditAccountNumber,
        accountBalance: unmask(`${accountBalance}`) || 0,
        amount: unmask(`${amount}`) || 0,
        dscFee: unmask(`${dscFee}`) || 0,
        custodianFee: unmask(`${custodianFee}`) || 0,
        isActual: newActual.current.checked,
        settlementDate,
        id,
      });
      setIsEditing(false);
    }
  };

  const handleSaveNewRow = (e) => {
    e.preventDefault();
    const creditorNameTemp = (
      creditorNameRef.current.value && creditorNameRef.current.value.trim()
    ) || '';
    const currentCreditorTemp = (
      currentCreditorRef.current.value && currentCreditorRef.current.value.trim()
    ) || '';
    const settlementIdTemp = (
      settlementIdRef.current.value && settlementIdRef.current.value.trim()
    ) || '';

    if (
      !creditorNameTemp ||
      !currentCreditorTemp ||
      !settlementIdTemp ||
      !creditAccountNumberRef.current.inputElement.value ||
      !unmask(accountBalanceRef.current.inputElement.value) ||
      settlementAmount.current.inputElement.value === '' ||
      (!actual.current.checked && !estimated.current.checked) ||
      dscFeeRef.current.inputElement.value === '' ||
      custodianFeeRef.current.inputElement.value === '' ||
      !settlementDateRef.current.inputElement.value
    ) {
      notification.current
      && notification.current.addNotification({
        message: 'All the fields are required',
        level: 'error',
        position: 'tc',
      });
      return false;
    }
    if (creditAccountNumberRef.current.inputElement.value.replace('_', '').length < 4) {
      notification.current
      && notification.current.addNotification({
        message: 'Account number is not valid',
        level: 'error',
        position: 'tc',
      });
      return false;
    }
    if (settlementDate.indexOf('_') !== -1 || !moment(settlementDateRef.current.inputElement.value, 'MM/DD/YYYY').isValid()) {
      notification.current
      && notification.current.addNotification({
        message: 'Date of Settlement is not valid',
        level: 'error',
        position: 'tc',
      });
    } else {
      props.saveRow({
        creditorName: creditorNameTemp,
        currentCreditor: currentCreditorTemp,
        settlementId: settlementIdTemp,
        creditAccountNumber: creditAccountNumberRef.current.inputElement.value,
        accountBalance: unmask(accountBalanceRef.current.inputElement.value) || 0,
        amount: unmask(settlementAmount.current.inputElement.value) || 0,
        dscFee: unmask(dscFeeRef.current.inputElement.value) || 0,
        custodianFee: unmask(custodianFeeRef.current.inputElement.value) || 0,
        isActual: actual.current.checked,
        settlementDate: settlementDateRef.current.inputElement.value,
      });
      setIsNew(false);
    }
  };

  const handleDeleteRow = (index, e) => {
    e.preventDefault();
    props.deleteRow(index);
  };

  const handleAddRow = (e) => {
    e.preventDefault();
    setIsNew(true);
  };

  const handleCancel = (e) => {
    e.preventDefault();

    setIsEditing(false);
    setIsNew(false);
  };

  const handleCancelNewRow = (e) => {
    e.preventDefault();
    setIsNew(false);
  };

  const handleInputChange = (name, e) => {
    e.preventDefault();
    const { value: targetValue } = e.target;

    switch (name) {
      case 'creditorName':
        setCreditorName(targetValue);
        break;
      case 'currentCreditor':
        setCurrentCreditor(targetValue);
        break;
      case 'settlementId':
        setSettlementId(targetValue);
        break;
      case 'creditAccountNumber':
        setCreditAccountNumber(targetValue);
        break;
      case 'accountBalance':
        setAccountBalance(targetValue);
        break;
      case 'dscFee':
        setDscFee(targetValue);
        break;
      case 'custodianFee':
        setCustodianFee(targetValue);
        break;
      case 'amount':
        setAmount(targetValue);
        break;
      case 'settlementDate':
        setSettlementDate(targetValue);
        break;
      default:
        break;
    }
  };

  return (
    <div className={style.table}>
      <NotificationSystem ref={notification} />
      { props.isLoading &&
        <Fragment>
          <div className={style.overlay} />
          <div className={style.loadingIndicator}>
            <Loading type="oval" width={80} height={80} fill="#06bd6c" />
          </div>
        </Fragment> }
      <div className={style.tableWrapper}>
        <div className={style.header}>
          {
            props.columns.map((item, index) => <div className={cn(style[`cell${index + 1}`], style.headerCell)} key={`header-${index}`}>{item.name}</div>)
          }
          <div className={cn(style.cell12, style.headerCell)}>&nbsp;</div>
        </div>
        <div className={style.content}>
          {
            rows.map((item, index) => (
              isEditing === index ?
                <div className={style.row} key={`row-${index}`}>
                  <div className={cn(style.cell1, style.cell)}>
                    <Input
                      value={creditorName || ''}
                      onChange={handleInputChange.bind(null, 'creditorName')}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell2, style.cell)}>
                    <Input
                      value={currentCreditor || ''}
                      onChange={handleInputChange.bind(null, 'currentCreditor')}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell3, style.cell)}>
                    <Input
                      value={settlementId || ''}
                      onChange={handleInputChange.bind(null, 'settlementId')}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell4, style.cell)}>
                    <Input
                      value={`${creditAccountNumber}` || ''}
                      isMasked={[/\d/, /\d/, /\d/, /\d/]}
                      onChange={handleInputChange.bind(null, 'creditAccountNumber')}
                      placeholder="_____"
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell5, style.cell)}>
                    <Input
                      value={`${accountBalance}` || ''}
                      isMasked={currencyMask}
                      onChange={handleInputChange.bind(null, 'accountBalance')}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell6, style.cell)}>
                    <Input
                      value={`${dscFee}` || ''}
                      isMasked={currencyMask}
                      onChange={handleInputChange.bind(null, 'dscFee')}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell7, style.cell)}>
                    <Input
                      value={`${custodianFee}` || ''}
                      isMasked={currencyMask}
                      onChange={handleInputChange.bind(null, 'custodianFee')}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell8, style.cell)}>
                    <Input
                      value={`${amount}` || ''}
                      isMasked={currencyMask}
                      onChange={handleInputChange.bind(null, 'amount')}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell9, style.cell)}>
                    <input type="radio" name="newEstimated" ref={newEstimated} defaultChecked={!item.isActual} />
                  </div>
                  <div className={cn(style.cell10, style.cell)}>
                    <input type="radio" name="newActual" ref={newActual} defaultChecked={item.isActual} />
                  </div>
                  <div className={cn(style.cell11, style.cell)}>
                    <Input
                      onChange={handleInputChange.bind(null, 'settlementDate')}
                      value={`${settlementDate}` || ''}
                      placeholder="MM/DD/YYYY"
                      isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                      noErrorMessage
                    />
                  </div>
                  <div className={cn(style.cell12, style.cell)}>
                    <ButtonLink onClick={handleUpdateRow.bind(null, index)}>
                      <Check className={style.editIcon} />
                    </ButtonLink>
                    <ButtonLink onClick={handleCancel}>
                      <Close className={style.deleteIcon} />
                    </ButtonLink>
                  </div>
                </div>
              :
                <div className={style.row} key={`row-${index}`}>
                  <div className={cn(style.cell1, style.cell)}><strong>{item.creditorName || '-'}</strong></div>
                  <div className={cn(style.cell2, style.cell)}><strong>{item.currentCreditor || '-'}</strong></div>
                  <div className={cn(style.cell3, style.cell)}><strong>{item.settlementId || '-'}</strong></div>
                  <div className={cn(style.cell4, style.cell)}>{item.creditAccountNumber || '-'}</div>
                  <div className={cn(style.cell5, style.cell)}>${(item.accountBalance && formatCurrency(item.accountBalance, 2)) || '-'}</div>
                  <div className={cn(style.cell6, style.cell)}>${(item.dscFee && formatCurrency(item.dscFee, 2)) || '-'}</div>
                  <div className={cn(style.cell7, style.cell)}>${(formatCurrency(item.custodianFee || 0, 2))}</div>
                  <div className={cn(style.cell8, style.cell)}>${(item.amount && formatCurrency(item.amount, 2)) || '-'}</div>
                  <div className={cn(style.cell9, style.cell)}>
                    {
                      !(item.isActual) ?
                        <Check className={style.editIcon} />
                      :
                        <Cancel className={style.deleteIcon} />
                    }
                  </div>
                  <div className={cn(style.cell10, style.cell)}>
                    {
                      (item.isActual) ?
                        <Check className={style.editIcon} />
                      :
                        <Cancel className={style.unCheck} />
                    }
                  </div>
                  <div className={cn(style.cell11, style.cell)}>{format(new Date(item.settlementDate), 'MM/dd/yyyy')}</div>
                  <div className={cn(style.cell12, style.cell)}>
                    {
                      !props.isSubmitted &&
                      <Fragment>
                        <ButtonLink onClick={handleEditRow.bind(null, index)}>
                          <Edit className={style.editIcon} />
                        </ButtonLink>
                        {
                          rows.length > 1 &&
                          <ButtonLink onClick={handleDeleteRow.bind(null, index)}>
                            <Delete className={style.deleteIcon} />
                          </ButtonLink>
                        }
                      </Fragment>
                    }
                  </div>
                </div>
            ))
          }
          { isNew &&
            <div className={style.row}>
              <div className={cn(style.cell1, style.cell, style.newField)}>
                <input type="text" ref={creditorNameRef} />
              </div>
              <div className={cn(style.cell2, style.cell, style.newField)}>
                <input type="text" ref={currentCreditorRef} />
              </div>
              <div className={cn(style.cell3, style.cell, style.newField)}>
                <input type="text" ref={settlementIdRef} />
              </div>
              <div className={cn(style.cell4, style.cell, style.newField)}>
                <MaskedInput
                  ref={creditAccountNumberRef}
                  isMasked={[/\d/, /\d/, /\d/, /\d/]}
                  noErrorMessage
                  placeholder="_____"
                />
              </div>
              <div className={cn(style.cell5, style.cell, style.newField)}>
                <MaskedInput
                  ref={accountBalanceRef}
                  isMasked={currencyMask}
                  noErrorMessage
                />
              </div>
              <div className={cn(style.cell6, style.cell, style.newField)}>
                <MaskedInput
                  ref={dscFeeRef}
                  isMasked={currencyMask}
                  noErrorMessage
                />
              </div>
              <div className={cn(style.cell7, style.cell, style.newField)}>
                <MaskedInput
                  ref={custodianFeeRef}
                  isMasked={currencyMask}
                  noErrorMessage
                />
              </div>
              <div className={cn(style.cell8, style.cell, style.newField)}>
                <MaskedInput
                  ref={settlementAmount}
                  isMasked={currencyMask}
                  noErrorMessage
                />
              </div>
              <div className={cn(style.cell9, style.cell, style.newField)}>
                <input type="radio" name="estmated" ref={estimated} />
              </div>
              <div className={cn(style.cell10, style.cell, style.newField)}>
                <input type="radio" name="estmated" ref={actual} />
              </div>
              <div className={cn(style.cell11, style.cell, style.newField)}>
                <MaskedInput
                  ref={settlementDateRef}
                  placeholder="MM/DD/YYYY"
                  isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                  noErrorMessage
                />
              </div>
              <div className={cn(style.cell12, style.cell, style.newField)}>
                <ButtonLink onClick={handleSaveNewRow}>
                  <Check className={style.editIcon} />
                </ButtonLink>
                <ButtonLink onClick={handleCancelNewRow}>
                  <Close className={style.deleteIcon} />
                </ButtonLink>
              </div>
            </div>
          }
        </div>
        <div className={style.footer}>
          <div className={style.footerCell1}>Total:</div>
          <div className={style.totalAccountBalance}>${(formatCurrency(sumBy(rows, item => Number(item.accountBalance)), 2)) || ' -'}</div>
          <div className={style.totalDscFee}>${(formatCurrency(sumBy(rows, item => Number(item.dscFee)), 2)) || ' -'}</div>
          <div className={style.totalSettlementAmount}>${(formatCurrency(sumBy(rows, item => Number(item.amount)), 2)) || ' -'}</div>
        </div>
      </div>
      {
        !props.isSubmitted &&
          <div className={style.buttonWrap}>
            <Button
              className={cn('button medium green', style.add)}
              onClick={handleAddRow}
              isDisabled={isNew}
            >
              Add New
            </Button>
          </div>
      }
    </div>
  );
};

EditableTable.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  saveRow: PropTypes.func.isRequired,
  updateRow: PropTypes.func.isRequired,
  deleteRow: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  testSubject: PropTypes.object,
};

EditableTable.defaultProps = {
  testSubject: null,
};

export default EditableTable;



// WEBPACK FOOTER //
// ./src/components/EditableTable/index.js